import React, { useState, useEffect, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import {
  subscribeToPmClients, clientOnBoard, createPmClient, updatePmClient, deletePmClient,
  subscribeToPmTasks, createPmTask, updatePmTask, deletePmTask, reorderPmTasks,
  applyTemplateToPmClient, addPmTaskComment, updatePmTaskComment, deletePmTaskComment,
  getPmTemplate, savePmTemplate, seedPmTemplates, DEFAULT_SEO_TASKS, DEFAULT_WEBSITE_TASKS,
} from '../services/pmBoard';
import { sendAlGarciaAlert, sendPhilAlert, newlyTagged } from '../services/pmEmail';

const BOARD_META = {
  'in-house': { title: 'In-House SEO Clients', templateId: 'seo' },
  'website':  { title: 'Website Design Projects', templateId: 'website' },
};

const STATUS_OPTIONS = [
  { value: '',        label: 'Blank',         bg: 'var(--bg-tertiary)', color: 'var(--text-muted)' },
  { value: 'working', label: 'Working on it', bg: '#7a4d00',            color: '#f0b429' },
  { value: 'hold',    label: 'Hold',          bg: '#5e1a1a',            color: '#f87171' },
  { value: 'na',      label: 'N/A',           bg: '#1c3a5e',            color: '#58a6ff' },
  { value: 'done',    label: 'Done',          bg: '#13432b',            color: '#3fb950' },
];
const statusMeta = (v) => STATUS_OPTIONS.find(s => s.value === v) || STATUS_OPTIONS[0];

// Timestamp <-> yyyy-mm-dd helpers for the date input
function tsToInput(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
}
function inputToTs(val) {
  if (!val) return null;
  const d = new Date(val + 'T12:00:00'); // noon to dodge TZ rollover
  return isNaN(d) ? null : Timestamp.fromDate(d);
}
function fmtCommentDate(ts) {
  const d = ts?.toDate ? ts.toDate() : new Date((ts?.seconds || 0) * 1000);
  return isNaN(d) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ProjectBoard({ board }) {
  const meta = BOARD_META[board] || BOARD_META['in-house'];
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedId, setExpandedId] = useState(() => localStorage.getItem(`pm_expanded_${board}`));
  const [modalOpen, setModalOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  useEffect(() => { seedPmTemplates(); }, []);

  useEffect(() => {
    setLoading(true);
    const unsub = subscribeToPmClients((data) => { setClients(data); setLoading(false); });
    return unsub;
  }, []);

  useEffect(() => {
    if (expandedId) localStorage.setItem(`pm_expanded_${board}`, expandedId);
    else localStorage.removeItem(`pm_expanded_${board}`);
  }, [expandedId, board]);

  // Reset transient UI when switching boards
  useEffect(() => { setSearch(''); setShowArchived(false); setExpandedId(localStorage.getItem(`pm_expanded_${board}`)); }, [board]);

  const onBoard = clients.filter(c => clientOnBoard(c, board));
  const counts = {
    inHouse: clients.filter(c => clientOnBoard(c, 'in-house') && !c.archived).length,
    website: clients.filter(c => clientOnBoard(c, 'website') && !c.archived).length,
  };
  const filtered = onBoard
    .filter(c => c.archived === showArchived)
    .filter(c => {
      const q = search.toLowerCase();
      return (c.name || '').toLowerCase().includes(q) || (c.contactName || '').toLowerCase().includes(q);
    });

  return (
    <div className="page" style={{ maxWidth: 1400 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{meta.title}</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: 'rgba(88,166,255,.1)', border: '1px solid rgba(88,166,255,.25)', color: 'var(--accent-blue)' }}>
              {counts.inHouse} In-House SEO
            </span>
            <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: 'rgba(188,140,255,.1)', border: '1px solid rgba(188,140,255,.25)', color: 'var(--accent-purple)' }}>
              {counts.website} Website Design
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 200 }}
          />
          <button className="btn btn-secondary btn-sm" onClick={() => setShowArchived(s => !s)}>
            {showArchived ? '← Active' : 'Archived'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setExpandedId(null)}>Collapse All</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSetupOpen(true)}>⚙ Setup</button>
          {!showArchived && (
            <button className="btn btn-primary btn-sm" onClick={() => setModalOpen(true)}>+ New Client</button>
          )}
        </div>
      </div>

      {/* Client list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>
            {showArchived ? 'No archived clients on this board' : 'No clients yet'}
          </div>
          {!showArchived && <div style={{ fontSize: 13, marginTop: 4 }}>Click “+ New Client” to add one.</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(client => (
            <ClientRow
              key={client.id}
              client={client}
              isExpanded={expandedId === client.id}
              onToggle={() => setExpandedId(prev => prev === client.id ? null : client.id)}
            />
          ))}
        </div>
      )}

      {modalOpen && <NewClientModal board={board} onClose={() => setModalOpen(false)} />}
      {setupOpen && <TemplateSetup templateId={meta.templateId} title={meta.title} onClose={() => setSetupOpen(false)} />}
    </div>
  );
}

// ── Client row ───────────────────────────────────────────────────────────────

function ClientRow({ client, isExpanded, onToggle }) {
  const [tasks, setTasks] = useState([]);
  const [newTaskName, setNewTaskName] = useState('');
  const [applying, setApplying] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [err, setErr] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => subscribeToPmTasks(client.id, setTasks), [client.id]);

  const doneCount = tasks.filter(t => t.status === 'done').length;
  const total = tasks.length;
  const progress = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const visibleTasks = hideCompleted ? tasks.filter(t => t.status !== 'done') : tasks;

  const upd = (field, value) => updatePmClient(client.id, { [field]: value });

  const handleApplyTemplate = async () => {
    if (!window.confirm('Apply the task template to this client?')) return;
    setApplying(true); setErr('');
    try { await applyTemplateToPmClient(client.id, client.board); }
    catch (e) { setErr(e.message); }
    finally { setApplying(false); }
  };

  const handleMarkAllDone = async () => {
    const pending = tasks.filter(t => t.status !== 'done');
    if (pending.length === 0) return;
    if (!window.confirm(`Mark all ${pending.length} pending tasks done for ${client.name}?`)) return;
    await Promise.all(pending.map(t => updatePmTask(t.id, { status: 'done' })));
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim()) return;
    await createPmTask({ clientId: client.id, name: newTaskName.trim(), status: '', owner: 'Phil', date: null, notes: '', order: tasks.length });
    setNewTaskName('');
  };

  const handleArchiveToggle = () => {
    upd('archived', !client.archived);
  };

  const handleDeleteClient = async () => {
    if (!window.confirm(`Delete "${client.name}" and ALL its tasks? This cannot be undone.`)) return;
    await deletePmClient(client.id);
  };

  // Native drag reorder
  const handleDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setOverIdx(null); return; }
    const order = [...visibleTasks];
    const [moved] = order.splice(dragIdx, 1);
    order.splice(dropIdx, 0, moved);
    setDragIdx(null); setOverIdx(null);
    await reorderPmTasks(order);
  };

  const inputStyle = { background: 'transparent', border: 'none', padding: 0, fontSize: 13, color: 'var(--text-secondary)' };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header row */}
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', background: isExpanded ? 'var(--bg-tertiary)' : 'transparent' }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 12, width: 12 }}>{isExpanded ? '▾' : '▸'}</span>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, minWidth: 140, flex: '0 0 auto' }}>{client.name}</span>

        {/* progress */}
        <div style={{ flex: 1, minWidth: 120, maxWidth: 240 }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>
            <span>{doneCount}/{total} done</span><span style={{ color: 'var(--accent-blue)' }}>{progress}%</span>
          </div>
          <div style={{ height: 4, background: 'var(--bg-tertiary)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: progress === 100 ? 'var(--accent-green)' : 'var(--accent-blue)', transition: 'width .3s' }} />
          </div>
        </div>

        {/* website + contact (inline editable) */}
        <input style={{ ...inputStyle, width: 150, color: 'var(--accent-blue)' }} value={client.website || ''} placeholder="website…"
          onClick={e => e.stopPropagation()} onChange={e => upd('website', e.target.value)} />
        <input style={{ ...inputStyle, width: 120 }} value={client.contactName || ''} placeholder="contact…"
          onClick={e => e.stopPropagation()} onChange={e => upd('contactName', e.target.value)} />

        {/* actions */}
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
          <IconBtn title="Mark all done" onClick={handleMarkAllDone}>✓</IconBtn>
          <IconBtn title={hideCompleted ? 'Show completed' : 'Hide completed'} active={hideCompleted} onClick={() => setHideCompleted(h => !h)}>{hideCompleted ? '🙈' : '👁'}</IconBtn>
          <IconBtn title="Apply template" onClick={handleApplyTemplate}>{applying ? '…' : '▦'}</IconBtn>
          <IconBtn title={client.archived ? 'Unarchive' : 'Archive'} active={client.archived} onClick={handleArchiveToggle}>🗄</IconBtn>
          <IconBtn title="Delete client" danger onClick={handleDeleteClient}>🗑</IconBtn>
        </div>
      </div>

      {/* Expanded tasks */}
      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-primary)', padding: '8px 0' }}>
          {err && <div className="error-box" style={{ margin: '8px 16px', fontSize: 12 }}>{err}</div>}
          {client.notes !== undefined && (
            <div style={{ padding: '6px 16px' }}>
              <input style={{ ...inputStyle, width: '100%', fontStyle: 'italic', color: 'var(--text-muted)' }} value={client.notes || ''} placeholder="Client notes…" onChange={e => upd('notes', e.target.value)} />
            </div>
          )}
          {visibleTasks.map((task, idx) => (
            <div
              key={task.id}
              onDragStart={() => setDragIdx(idx)}
              onDragOver={e => { e.preventDefault(); setOverIdx(idx); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              onDrop={() => handleDrop(idx)}
              style={{ borderTop: overIdx === idx && dragIdx !== idx ? '2px solid var(--accent-blue)' : '2px solid transparent', opacity: dragIdx === idx ? 0.4 : 1 }}
            >
              <TaskRow task={task} clientName={client.name} />
            </div>
          ))}

          {/* Add task */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px' }}>
            <span style={{ color: 'var(--accent-blue)', fontSize: 16 }}>+</span>
            <input
              type="text"
              value={newTaskName}
              onChange={e => setNewTaskName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddTask(); }}
              placeholder="Add a task…  (Enter)"
              style={{ ...inputStyle, flex: 1, fontSize: 13, color: 'var(--text-secondary)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, title, onClick, active, danger }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: 13, lineHeight: 1,
        background: active ? 'rgba(88,166,255,.12)' : 'transparent',
        border: '1px solid transparent', color: danger ? 'var(--accent-red)' : (active ? 'var(--accent-blue)' : 'var(--text-muted)'),
      }}
    >{children}</button>
  );
}

// ── Task row ─────────────────────────────────────────────────────────────────

function TaskRow({ task, clientName }) {
  const [localNotes, setLocalNotes] = useState(task.notes || '');
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => { setLocalNotes(task.notes || ''); }, [task.notes]);

  const s = statusMeta(task.status);

  const fireTagAlerts = (tagged, taggedIn, notes) => {
    if (tagged.al) sendAlGarciaAlert({ taskName: task.name, clientName, notes, taggedIn });
    if (tagged.phil) sendPhilAlert({ taskName: task.name, clientName, notes, taggedIn });
  };

  const handleOwnerChange = (owner) => {
    const oldO = (task.owner || '').toLowerCase();
    const newO = owner.toLowerCase();
    updatePmTask(task.id, { owner });
    if (oldO === newO) return;
    if (newO === 'al garcia' || newO === 'al') sendAlGarciaAlert({ taskName: task.name, clientName, notes: task.notes, taggedIn: 'owner' });
    if (newO === 'phil garcia' || newO === 'phil') sendPhilAlert({ taskName: task.name, clientName, notes: task.notes, taggedIn: 'owner' });
  };

  const saveNotes = () => {
    fireTagAlerts(newlyTagged(task.notes, localNotes), 'notes', localNotes);
    updatePmTask(task.id, { notes: localNotes });
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await addPmTaskComment(task.id, newComment.trim());
      fireTagAlerts(newlyTagged('', newComment), 'notes', newComment.trim());
      setNewComment('');
    } finally { setSubmitting(false); }
  };

  const handleDelete = () => { if (window.confirm('Delete this task?')) deletePmTask(task.id); };

  const cellInput = { background: 'transparent', border: 'none', padding: 0, fontSize: 12, color: 'var(--text-secondary)' };
  const notesDirty = localNotes !== (task.notes || '');
  const myUid = auth.currentUser?.uid;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', flexWrap: 'wrap' }}>
        <span draggable style={{ cursor: 'grab', color: 'var(--text-muted)', fontSize: 14, userSelect: 'none' }} title="Drag to reorder">⠿</span>

        <input
          style={{ ...cellInput, flex: 2, minWidth: 160, color: 'var(--text-primary)', fontWeight: 500, fontSize: 13 }}
          value={task.name}
          onChange={e => updatePmTask(task.id, { name: e.target.value })}
          placeholder="Task name…"
        />

        {/* owner */}
        <input
          style={{ ...cellInput, width: 90 }}
          value={task.owner || ''}
          onChange={e => handleOwnerChange(e.target.value)}
          placeholder="Owner…"
        />

        {/* status */}
        <select
          value={task.status || ''}
          onChange={e => updatePmTask(task.id, { status: e.target.value })}
          style={{ width: 130, flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--border)', background: s.bg, color: s.color, cursor: 'pointer' }}
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value} style={{ background: 'var(--bg-input)', color: 'var(--text-primary)' }}>{o.label}</option>)}
        </select>

        {/* date */}
        <input
          type="date"
          value={tsToInput(task.date)}
          onChange={e => updatePmTask(task.id, { date: inputToTs(e.target.value) })}
          style={{ width: 140, flexShrink: 0, fontSize: 11, padding: '3px 6px', color: 'var(--text-muted)' }}
        />

        {/* notes */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 2, minWidth: 140 }}>
          <input
            style={{ ...cellInput, flex: 1, fontStyle: 'italic', color: 'var(--text-muted)' }}
            value={localNotes}
            onChange={e => setLocalNotes(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveNotes(); }}
            placeholder="Notes…"
          />
          {notesDirty && <button className="btn btn-sm btn-primary" style={{ padding: '2px 8px', fontSize: 11 }} onClick={saveNotes}>Save</button>}
        </div>

        {/* actions */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <IconBtn title="Comments" active={commentsOpen} onClick={() => setCommentsOpen(o => !o)}>
            💬{task.comments?.length ? <span style={{ fontSize: 9 }}> {task.comments.length}</span> : ''}
          </IconBtn>
          <IconBtn title="Delete task" danger onClick={handleDelete}>🗑</IconBtn>
        </div>
      </div>

      {/* Comments */}
      {commentsOpen && (
        <div style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', padding: '12px 16px 12px 44px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Task Comments</div>
          {(task.comments || []).length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 10 }}>No comments yet.</div>}
          {(task.comments || []).map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>
                {(c.userName || '?').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{c.userName}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{fmtCommentDate(c.createdAt)}</span>
                  {myUid === c.userId && editingId !== c.id && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button style={linkBtn} onClick={() => { setEditingId(c.id); setEditingText(c.text); }}>Edit</button>
                      <button style={{ ...linkBtn, color: 'var(--accent-red)' }} onClick={() => deletePmTaskComment(task.id, c.id)}>Delete</button>
                    </span>
                  )}
                </div>
                {editingId === c.id ? (
                  <div>
                    <textarea value={editingText} onChange={e => setEditingText(e.target.value)} style={{ width: '100%', minHeight: 56, fontSize: 12 }} />
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button className="btn btn-sm btn-primary" onClick={async () => { await updatePmTaskComment(task.id, c.id, editingText); setEditingId(null); }}>Save</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{renderMentions(c.text)}</div>
                )}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Write a response… (@Al or @Phil to notify)"
              style={{ flex: 1, minHeight: 50, fontSize: 12 }}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAddComment(); } }}
            />
            <button className="btn btn-primary btn-sm" disabled={!newComment.trim() || submitting} onClick={handleAddComment}>
              {submitting ? '…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-muted)' };

// Highlight @Al / @Phil mentions and turn URLs into clickable links in comment text
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const MENTION_RE = /(@al garcia|@phil garcia|@al|@phil)/gi;

function renderMentions(text) {
  // Split on URLs first, then split the non-URL pieces on @mentions.
  return (text || '').split(URL_RE).map((chunk, i) => {
    if (URL_RE.test(chunk)) {
      URL_RE.lastIndex = 0; // reset global regex after test()
      const href = chunk.startsWith('http') ? chunk : `https://${chunk}`;
      return (
        <a
          key={`u${i}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--accent-blue)', textDecoration: 'underline', wordBreak: 'break-all' }}
        >{chunk}</a>
      );
    }
    URL_RE.lastIndex = 0;
    return chunk.split(MENTION_RE).map((p, j) =>
      /^@(al|phil)/i.test(p)
        ? <span key={`m${i}-${j}`} style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{p}</span>
        : <React.Fragment key={`t${i}-${j}`}>{p}</React.Fragment>
    );
  });
}

// ── New client modal ─────────────────────────────────────────────────────────

function NewClientModal({ board, onClose }) {
  const [form, setForm] = useState({ name: '', website: '', contactName: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try { await createPmClient({ ...form, board }); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={overlay}>
      <div className="modal card" onClick={e => e.stopPropagation()} style={{ width: 'min(520px, 92vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Add New Client</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="form-field"><label>Company Name</label>
            <input autoFocus type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Adaptive Information Systems" required /></div>
          <div className="form-field"><label>Website</label>
            <input type="url" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://…" /></div>
          <div className="form-field"><label>Contact Name</label>
            <input type="text" value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="e.g. Jesse Alvarado" /></div>
          <div className="form-field"><label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Initial client notes…" /></div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Creating…' : 'Create Client'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Template setup (per board) ───────────────────────────────────────────────

function TemplateSetup({ templateId, title, onClose }) {
  const [tasks, setTasks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dragIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => { getPmTemplate(templateId).then(setTasks); }, [templateId]);

  const uid = () => Math.random().toString(36).substring(2, 10);
  const update = (id, field, val) => setTasks(ts => ts.map(t => t.id === id ? { ...t, [field]: val } : t));
  const remove = (id) => setTasks(ts => ts.filter(t => t.id !== id));
  const add = () => setTasks(ts => [...ts, { id: uid(), name: '', defaultOwner: 'Phil' }]);

  const reset = () => {
    if (!window.confirm('Reset this template to the default task list?')) return;
    const defaults = templateId === 'seo' ? DEFAULT_SEO_TASKS : DEFAULT_WEBSITE_TASKS;
    setTasks(defaults.map(name => ({ id: uid(), name, defaultOwner: 'Phil' })));
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try { await savePmTemplate(templateId, tasks); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    finally { setSaving(false); }
  };

  const drop = (dropIdx) => {
    const from = dragIdx.current;
    if (from === null || from === dropIdx) { dragIdx.current = null; setOverIdx(null); return; }
    setTasks(ts => { const a = [...ts]; const [m] = a.splice(from, 1); a.splice(dropIdx, 0, m); return a; });
    dragIdx.current = null; setOverIdx(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={overlay}>
      <div className="modal card" onClick={e => e.stopPropagation()} style={{ width: 'min(640px, 94vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Task Template — {title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          These tasks are applied when you click “Apply template” on a client. Drag to reorder.
        </p>

        {tasks === null ? (
          <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner"></div></div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
            {tasks.map((t, idx) => (
              <div
                key={t.id}
                draggable
                onDragStart={() => { dragIdx.current = idx; }}
                onDragOver={e => { e.preventDefault(); setOverIdx(idx); }}
                onDrop={() => drop(idx)}
                onDragEnd={() => { dragIdx.current = null; setOverIdx(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderTop: overIdx === idx ? '2px solid var(--accent-blue)' : '1px solid var(--border)', borderRadius: 6, padding: '8px 10px' }}
              >
                <span style={{ cursor: 'grab', color: 'var(--text-muted)', userSelect: 'none' }}>⠿</span>
                <div style={{ flex: 1 }}>
                  <input style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 13, color: 'var(--text-primary)', width: '100%' }}
                    value={t.name} placeholder="Task name…" onChange={e => update(t.id, 'name', e.target.value)} />
                  <input style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 11, color: 'var(--text-muted)', width: '100%', marginTop: 2 }}
                    value={t.defaultOwner} placeholder="Default owner…" onChange={e => update(t.id, 'defaultOwner', e.target.value)} />
                </div>
                <button onClick={() => remove(t.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }} title="Remove">🗑</button>
              </div>
            ))}
            <button onClick={add} className="btn btn-secondary btn-sm" style={{ width: '100%', borderStyle: 'dashed' }}>+ Add Task</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={reset}>Reset to default</button>
          {saved && <span style={{ fontSize: 12, color: 'var(--accent-green)' }}>✓ Saved</span>}
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={save} disabled={saving || tasks === null}>{saving ? 'Saving…' : 'Save Template'}</button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
